import type {
  ProposalNodeSet,
  RfqPackage,
  RfqProject,
  RfqSubmission,
  TimberSupplierStructureRef,
} from '../types';
import { inviteHasStructureScope } from './timberRfqStructureScope';
import { activeScopeStructures } from './rfqPackageArchivedStructures';

const FULL_SCOPE_TOKEN = 'full';

/** Enabled proposal / timber-list structures for RFQ publish pickers. */
export function structureRefsFromNodeSets(
  nodeSets: ProposalNodeSet[] | null | undefined,
): TimberSupplierStructureRef[] {
  if (!nodeSets?.length) return [];
  return normalizeQuotedStructures(
    nodeSets
      .filter((ns) => ns.enabled !== false)
      .map((ns) => ({ id: ns.id, name: (ns.name || '').trim() || ns.id })),
  );
}

/** Sorted unique structure refs by id (stable stream keys). */
export function normalizeQuotedStructures(
  refs: TimberSupplierStructureRef[] | null | undefined,
): TimberSupplierStructureRef[] {
  if (!refs?.length) return [];
  const byId = new Map<string, TimberSupplierStructureRef>();
  for (const ref of refs) {
    const id = (ref.id || '').trim();
    if (!id) continue;
    const name = (ref.name || '').trim() || id;
    if (!byId.has(id)) byId.set(id, { id, name });
  }
  return Array.from(byId.values()).sort((a, b) =>
    a.id.localeCompare(b.id, undefined, { sensitivity: 'base' }),
  );
}

/** True when the quote has no structure links (covers full assigned scope). */
export function isTimberQuoteFullScope(
  sub: Pick<RfqSubmission, 'quotedStructures'> | null | undefined,
): boolean {
  return normalizeQuotedStructures(sub?.quotedStructures).length === 0;
}

export function timberQuoteStreamKeyFromParts(
  species: string | null | undefined,
  structures?: TimberSupplierStructureRef[] | null,
): string {
  const sp = (species || '').trim().toLowerCase() || '_';
  const norm = normalizeQuotedStructures(structures);
  if (!norm.length) return `${sp}|${FULL_SCOPE_TOKEN}`;
  return `${sp}|${norm.map((s) => s.id).join(',')}`;
}

/**
 * Stream key for grouping timber quote history.
 * Same species + same structure set (or both full-scope) share one stream.
 */
type TimberQuoteStreamSubmission = Pick<
  RfqSubmission,
  'quotedSpecies' | 'quotedStructures' | 'fileName'
>;

export function timberQuoteStreamKey<T extends TimberQuoteStreamSubmission>(
  sub: T,
  resolveSpecies?: (sub: T) => string | null | undefined,
): string {
  const species =
    (resolveSpecies ? resolveSpecies(sub) : null)
    ?? sub.quotedSpecies
    ?? '';
  return timberQuoteStreamKeyFromParts(species, sub.quotedStructures);
}

/** Structure ids this quote covers given the supplier’s assigned scope. */
export function submissionCoversStructureIds(
  sub: Pick<RfqSubmission, 'quotedStructures'>,
  assignedStructureIds: string[],
): string[] {
  const assigned = assignedStructureIds.map((id) => id.trim()).filter(Boolean);
  if (isTimberQuoteFullScope(sub)) {
    return assigned.length ? [...assigned] : [];
  }
  const linked = normalizeQuotedStructures(sub.quotedStructures).map((s) => s.id);
  if (!assigned.length) return linked;
  const assignedSet = new Set(assigned);
  return linked.filter((id) => assignedSet.has(id));
}

function setsOverlap(a: string[], b: string[]): boolean {
  if (!a.length || !b.length) return false;
  const setB = new Set(b);
  return a.some((id) => setB.has(id));
}

/**
 * When approving {@link approving}, clear approvals on other candidates that claim
 * any of the same structures (including full-scope vs structure-specific conflicts).
 * Does not include {@link approving} itself.
 */
export function approvalClearIdsForTimberQuote(input: {
  approving: RfqSubmission;
  candidates: RfqSubmission[];
  assignedStructureIds: string[];
}): string[] {
  const approvingId = input.approving.id;
  const approvingCovers = submissionCoversStructureIds(
    input.approving,
    input.assignedStructureIds,
  );
  // Full-scope with unknown assignment: clear every other candidate (whole package).
  const clearAllOthers =
    isTimberQuoteFullScope(input.approving) && input.assignedStructureIds.length === 0;

  return input.candidates
    .filter((c) => {
      if (!c.id || c.id === approvingId) return false;
      if (clearAllOthers) return true;
      const otherCovers = submissionCoversStructureIds(c, input.assignedStructureIds);
      if (
        isTimberQuoteFullScope(input.approving)
        && !isTimberQuoteFullScope(c)
        && input.assignedStructureIds.length > 0
      ) {
        // Full-scope approval conflicts with any structure-specific quote in assigned set.
        return otherCovers.length > 0;
      }
      if (
        !isTimberQuoteFullScope(input.approving)
        && isTimberQuoteFullScope(c)
        && input.assignedStructureIds.length > 0
      ) {
        return true;
      }
      return setsOverlap(approvingCovers, otherCovers);
    })
    .map((c) => c.id!);
}

/** Human label for quote structure scope chips. */
export function timberQuoteStructureScopeLabel(
  sub: Pick<RfqSubmission, 'quotedStructures'>,
): string {
  const norm = normalizeQuotedStructures(sub.quotedStructures);
  if (!norm.length) return 'Full scope';
  return norm.map((s) => s.name || s.id).join(', ');
}

/**
 * Staff/supplier UI label when the submission omits `quotedStructures` (legacy uploads,
 * supplier portal “full assigned scope”) — infer from the RFQ invite / package scope instead
 * of showing “Full scope” for a 1-of-N structure request.
 */
export function timberQuoteStructureScopeDisplayLabel(
  sub: Pick<RfqSubmission, 'quotedStructures'>,
  assignableStructures: TimberSupplierStructureRef[],
  totalStructureCount?: number,
): string {
  const quoted = normalizeQuotedStructures(sub.quotedStructures);
  if (quoted.length > 0) {
    return quoted.map((s) => s.name || s.id).join(', ');
  }
  const assigned = normalizeQuotedStructures(assignableStructures);
  if (!assigned.length) return 'Full scope';
  const total = totalStructureCount ?? 0;
  if (total > 0 && assigned.length >= total) return 'Full scope';
  if (assigned.length === 1) return assigned[0].name || assigned[0].id;
  return assigned.map((s) => s.name || s.id).join(', ');
}

/**
 * Structures a supplier may link on a quote: package snapshot first, else invite selection,
 * else CNC payload structures, else proposal node sets on the package (full-scope invites),
 * else optional {@link fallbackStructures} (e.g. live proposal builder structures).
 */
export function timberQuoteAssignableStructures(
  project: Pick<RfqProject, 'timberSupplierInvites'>,
  orgId: string,
  pkg?: Pick<RfqPackage, 'scopeStructures' | 'archivedStructureIds' | 'proposalSnapshot' | 'cncPackagePayload'> | null,
  fallbackStructures?: TimberSupplierStructureRef[] | null,
): TimberSupplierStructureRef[] {
  if (pkg?.scopeStructures?.length) {
    return normalizeQuotedStructures(activeScopeStructures(pkg.scopeStructures, pkg.archivedStructureIds));
  }
  const invite = (project.timberSupplierInvites ?? []).find((i) => i.supplierOrgId === orgId);
  if (inviteHasStructureScope(invite)) {
    return normalizeQuotedStructures(invite!.structures);
  }
  if (pkg?.cncPackagePayload?.structures?.length) {
    return normalizeQuotedStructures(pkg.cncPackagePayload.structures);
  }
  const nodeSets = (pkg?.proposalSnapshot?.nodeSets ?? []).filter((ns) => ns.enabled !== false);
  if (nodeSets.length) {
    return normalizeQuotedStructures(
      nodeSets.map((ns) => ({ id: ns.id, name: (ns.name || '').trim() || ns.id })),
    );
  }
  if (fallbackStructures?.length) {
    return normalizeQuotedStructures(fallbackStructures);
  }
  return [];
}

/**
 * Structures staff may pick when linking a quote PDF to a structure.
 * Unions RFQ/package-assigned scope with live proposal structures so PM edits
 * are not limited to a single scoped package row (common for per-structure CNC uploads).
 */
export function timberQuoteStructurePickerOptions(
  project: Pick<RfqProject, 'timberSupplierInvites'>,
  orgId: string,
  pkg?: Pick<RfqPackage, 'scopeStructures' | 'proposalSnapshot' | 'cncPackagePayload'> | null,
  fallbackStructures?: TimberSupplierStructureRef[] | null,
): TimberSupplierStructureRef[] {
  const scoped = timberQuoteAssignableStructures(project, orgId, pkg, null);
  const proposal = normalizeQuotedStructures(fallbackStructures);
  if (!proposal.length) {
    return timberQuoteAssignableStructures(project, orgId, pkg, fallbackStructures);
  }
  return normalizeQuotedStructures([...scoped, ...proposal]);
}

/**
 * Resolve `quotedStructures` on create when the user omitted the picker.
 * Single-structure packages default to that structure; multi-structure packages require an explicit pick.
 */
export function resolveSubmissionQuotedStructures(input: {
  selected?: TimberSupplierStructureRef[] | null;
  packageScope?: TimberSupplierStructureRef[] | null;
  pickerOptions?: TimberSupplierStructureRef[] | null;
}): TimberSupplierStructureRef[] | undefined {
  const selected = normalizeQuotedStructures(input.selected);
  if (selected.length) return selected;
  const pkgScope = normalizeQuotedStructures(input.packageScope);
  if (pkgScope.length === 1) return pkgScope;
  const options = normalizeQuotedStructures(input.pickerOptions);
  if (options.length === 1) return options;
  return undefined;
}

/** When approving, stamp single-structure package scope onto submissions missing quotedStructures. */
export function quotedStructuresPatchOnApprove(
  sub: Pick<RfqSubmission, 'quotedStructures'>,
  pkg?: Pick<RfqPackage, 'scopeStructures'> | null,
): TimberSupplierStructureRef[] | undefined {
  if (normalizeQuotedStructures(sub.quotedStructures).length) return undefined;
  const scope = normalizeQuotedStructures(pkg?.scopeStructures);
  return scope.length === 1 ? scope : undefined;
}

/** Assigned structure ids for approval overlap (empty = unknown / treat full-scope specially). */
export function timberQuoteAssignedStructureIds(
  project: Pick<RfqProject, 'timberSupplierInvites'>,
  orgId: string,
  pkg?: Pick<RfqPackage, 'scopeStructures' | 'proposalSnapshot' | 'cncPackagePayload'> | null,
  fallbackStructures?: TimberSupplierStructureRef[] | null,
): string[] {
  return timberQuoteAssignableStructures(project, orgId, pkg, fallbackStructures).map((s) => s.id);
}

/**
 * Whether every assigned structure is covered by at least one approved quote.
 * When assignment is unknown (empty), falls back to “every primary stream approved”.
 */
export function timberAssignedStructuresFullyCovered(input: {
  approvedSubs: RfqSubmission[];
  assignedStructureIds: string[];
  primaryStreamCount: number;
}): boolean {
  const { approvedSubs, assignedStructureIds, primaryStreamCount } = input;
  if (assignedStructureIds.length === 0) {
    return primaryStreamCount > 0 && approvedSubs.length >= primaryStreamCount;
  }
  const covered = new Set<string>();
  for (const sub of approvedSubs) {
    for (const id of submissionCoversStructureIds(sub, assignedStructureIds)) {
      covered.add(id);
    }
  }
  return assignedStructureIds.every((id) => covered.has(id));
}
